import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import { FiArrowLeft } from 'react-icons/fi';
import { withAccount, withTranslation } from 'components/hoc';
import { AuthGatedTheme, useEffectiveDark } from '../common/AuthGatedTheme';

const PageShell = styled.main`
  min-height: 64vh;
  padding: 96px clamp(22px, 5vw, 90px);
  display: flex;
  align-items: center;
  background: ${({ $dark }) =>
    $dark
      ? 'linear-gradient(180deg, #0e1526 0%, #1c2741 100%)'
      : 'linear-gradient(180deg, #f6f9ff 0%, #ffffff 100%)'};
`;

const Content = styled.section`
  width: 100%;
  max-width: 760px;
`;

const Eyebrow = styled.p`
  margin: 0 0 14px;
  color: ${({ $dark }) => ($dark ? '#9fc0ff' : '#2f68b8')};
  font-size: 13px;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Heading = styled.h1`
  margin: 0;
  color: ${({ $dark }) => ($dark ? '#f4f8ff' : '#073b78')};
  font-size: clamp(2.35rem, 5vw, 4.5rem);
  line-height: 1.05;
  font-weight: 800;
`;

const Description = styled.p`
  max-width: 560px;
  margin: 22px 0 34px;
  color: ${({ $dark }) => ($dark ? '#c2d0ec' : '#52678b')};
  font-size: 17px;
  line-height: 1.75;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 8px;
  color: #ffffff;
  background: #073b78;
  text-decoration: none;
  font-size: 14px;
  font-weight: 700;
  transition: background 0.2s ease, transform 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: #ffffff;
    background: #0b4d98;
    transform: translateY(-1px);
  }
`;

const pageTitleByPath = {
  '/service': 'Service',
  '/features': 'Features',
  '/security-hub': 'Security Hub',
  '/modules': 'Modules',
  '/roadmap': 'Roadmap',
  '/about-us': 'About Us',
  '/contact': 'Contact',
  '/documentation': 'Documentation',
  '/community-forum': 'Community Forum',
  '/api-status': 'API Status',
  '/help-center': 'Help Center',
};

const socialTitleByPath = {
  '/social/x': 'X',
  '/social/linkedin': 'LinkedIn',
  '/social/facebook': 'Facebook',
};

const getPageTitle = (pathname) => {
  const normalizedPath = pathname.replace(/\/$/, '') || '/';
  return pageTitleByPath[normalizedPath] || socialTitleByPath[normalizedPath] || 'Coming soon';
};

const ComingSoon = (props) => {
  const { tr } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const isAuth = Boolean(props.account && props.account.user && props.account.user.isAuth);
  const effectiveDark = useEffectiveDark(isAuth);
  const translate = (key) => (isAuth ? tr(key) : t(key));
  const pageTitle = getPageTitle(location.pathname);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <AuthGatedTheme isAuth={isAuth}>
      <PageShell $dark={effectiveDark}>
        <Content>
          <Eyebrow $dark={effectiveDark}>{translate('Coming soon')}</Eyebrow>
          <Heading $dark={effectiveDark}>{translate(pageTitle)}</Heading>
          <Description $dark={effectiveDark}>
            {translate('This page is being prepared. Please check back soon.')}
          </Description>
          <BackLink to="/">
            <FiArrowLeft aria-hidden="true" />
            {translate('Back to home')}
          </BackLink>
        </Content>
      </PageShell>
    </AuthGatedTheme>
  );
};

export default compose(withTranslation, withAccount)(ComingSoon);
